import React, { useEffect, useState } from "react";
import "./Checkout.css";
import { getAnonUserId, getHostName, formatPKR } from "../helpers/utils";
import axios from "axios";
import ReactPixel from "react-facebook-pixel";
import { Container } from "react-bootstrap";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Accordion from "react-bootstrap/Accordion";

export default function CheckoutPage() {
  let hostname = getHostName();
  const navigate = useNavigate();

  const [cloader, setCLoader] = useState(false);

  const [cartItems, setCartItems] = useState([]);
  const [cartItemsCounter, setCartItemsCounter] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const taxRate = 0.0;

  useEffect(() => {
    const getAllCartListData = () => {
      let anonUserId = getAnonUserId();
      axios
        .post(`${hostname}/api/cart/get`, { annoUserDeviceId: anonUserId })
        .then((res) => {
          let arr = res.data.filter((v) => v.status);
          setCartItems(arr);
        })
        .catch((err) => console.log(err));
    };

    const getCartCounterData = () => {
      let anonUserId = getAnonUserId();
      axios
        .get(`${hostname}/api/cart/count?annoUserDeviceId=${anonUserId}`)
        .then((res) => setCartItemsCounter(res.data.count))
        .catch((err) => {
          if ("response" in err)
            toast.error(err.response.data.error, { theme: "colored" });
          else toast.error("Something went wrong!", { theme: "colored" });
        });
    };

    getCartCounterData();
    getAllCartListData();
  }, []);

  useEffect(() => {
    const subtotalCalc = cartItems.reduce(
      (acc, item) => acc + item.product.price * item.quantity,
      0
    );
    setSubtotal(subtotalCalc);
  }, [cartItems]);

  const total = (subtotal + subtotal * taxRate).toFixed(2);

  const handleCheckoutSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setCLoader(true);
      let anonUserId = getAnonUserId();
      // Build payload and omit empty referral code to avoid backend validation errors
      const payload = { ...values, annoUserDeviceId: anonUserId };
      if (!payload.referral_code_used || payload.referral_code_used.trim() === "") {
        delete payload.referral_code_used;
      }

      // generate a unique event id for deduplication (frontend Pixel + server CAPI)
      const generateEventId = () => {
        try {
          if (window && window.crypto && typeof window.crypto.randomUUID === "function")
            return window.crypto.randomUUID();
        } catch (e) {}
        // fallback simple UUID v4
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
      };

      const eventId = generateEventId();
      payload.eventId = eventId;

      await axios.post(`${hostname}/api/checkout/submit`, payload);

      // Fire Purchase event to Meta Pixel (if configured)
      try {
        const shippingFee = values?.shipping_method === "cod" ? 200 : 0;
        const purchaseValue = subtotal + subtotal * taxRate + shippingFee;
        // sanitize currency and ensure numeric value
        const sanitizeCurrency = (c) => {
          if (!c) return "PKR";
          const cleaned = c.toString().trim().toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
          return /^[A-Z]{3}$/.test(cleaned) ? cleaned : "PKR";
        };

        const currencyToSend = sanitizeCurrency("PKR");
        const numericValue = parseFloat(purchaseValue.toFixed(2));

        // Log payload for debugging Meta Pixel parameter issues
        try {
          console.debug("Pixel Purchase payload:", { value: numericValue, currency: currencyToSend, eventId: payload.eventId });
        } catch (e) {}

        if (ReactPixel && typeof ReactPixel.track === "function") {
          ReactPixel.track(
            "Purchase",
            {
              value: numericValue,
              currency: currencyToSend,
            },
            { eventID: payload.eventId }
          );
        }
      } catch (e) {
        console.warn("Pixel track error:", e);
      }

      toast.success("Order placed successfully!");
      toast.success("A confirmation email will reach you shortly.");
      resetForm();
      setSubmitting(false);
      setCLoader(false);
      setTimeout(() => navigate("/", { replace: true }), 500);
    } catch (err) {
      console.error("Order submit error:", err);
      // Prefer showing server-provided error message when available
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message;
      if (serverMsg) toast.error(serverMsg, { theme: "colored" });
      else toast.error("Something went wrong while placing the order.", { theme: "colored" });
    } finally {
      setSubmitting(false);
      setCLoader(false);
    }
  };

  return (
    <Container>
      <Formik
        initialValues={{
          email: "",
          country: "Pakistan",
          first_name: "",
          last_name: "",
          address: "",
          city: "",
          postal: "",
          phone: "",
          referral_code_used: "",
          shipping_method: "bank_transfer",
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("Enter a valid email")
            .matches(
              /^[a-zA-Z0-9._%+-]+@gmail\.com$/i,
              "Only @gmail.com emails allowed"
            )
            .required("Email is required"),
          country: Yup.string().required("Country required"),
          first_name: Yup.string().required("First name required"),
          last_name: Yup.string().required("Last name required"),
          address: Yup.string().required("Address required"),
          city: Yup.string().required("City required"),
          postal: Yup.string().required("Postal code required"),
          phone: Yup.string().required("Phone is required"),
          // referral_code_used is optional for new customers; if provided, server logic will handle validation
          referral_code_used: Yup.string(),
        })}
        onSubmit={handleCheckoutSubmit}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue }) => (
          <Form>
            <div className="shopify-checkout">
              {/* LEFT PANEL */}
              <div className="checkout-left">
                <div className="brand">
                  <span onClick={() => navigate("/", { replace: true })}>
                    <i
                      className="bi bi-arrow-left-circle"
                      style={{ cursor: "pointer", fontSize: "22px" }}
                    ></i>
                  </span>
                  <h5 className="fw-bold ms-3">AuraSkinPremium</h5>
                </div>

                {/* Contact */}
                <div className="form-section">
                  <label className="d-flex justify-content-between align-items-center">
                    <span>Contact</span>
                    <Link to={"/login"} className="login-link">
                      Log in
                    </Link>
                  </label>
                  <Field
                    type="email"
                    name="email"
                    className={`form-control ${
                      touched.email && errors.email ? "is-invalid" : ""
                    }`}
                    placeholder="Email address"
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>

                {/* Delivery */}
                <br />
                <div className="form-section">
                  <label>Delivery</label>
                  <Field
                    as="select"
                    name="country"
                    className={`form-select ${
                      touched.country && errors.country ? "is-invalid" : ""
                    }`}
                  >
                    <option value="Pakistan">Pakistan</option>
                  </Field>
                  <ErrorMessage
                    name="country"
                    component="div"
                    className="invalid-feedback"
                  />

                  <div className="row-2">
                    <Field
                      type="text"
                      name="first_name"
                      className={`form-control ${
                        touched.first_name && errors.first_name
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="First Name"
                    />
                    <Field
                      type="text"
                      name="last_name"
                      className={`form-control ${
                        touched.last_name && errors.last_name
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="Last Name"
                    />
                  </div>
                  <ErrorMessage
                    name="first_name"
                    component="div"
                    className="invalid-feedback"
                  />
                  <ErrorMessage
                    name="last_name"
                    component="div"
                    className="invalid-feedback"
                  />

                  <Field
                    type="text"
                    name="address"
                    className={`form-control ${
                      touched.address && errors.address ? "is-invalid" : ""
                    }`}
                    placeholder="Address"
                  />
                  {/* <ErrorMessage
                    name="address"
                    component="div"
                    className="invalid-feedback"
                  /> */}

                  <div className="row-2">
                    <Field
                      type="text"
                      name="city"
                      className={`form-control ${
                        touched.city && errors.city ? "is-invalid" : ""
                      }`}
                      placeholder="City"
                    />
                    <Field
                      type="text"
                      name="postal"
                      className={`form-control ${
                        touched.postal && errors.postal ? "is-invalid" : ""
                      }`}
                      placeholder="Postal code"
                    />
                  </div>
                  {/* <ErrorMessage
                    name="city"
                    component="div"
                    className="invalid-feedback"
                  />
                  <ErrorMessage
                    name="postal"
                    component="div"
                    className="invalid-feedback"
                  /> */}

                  <Field
                    type="text"
                    name="phone"
                    className={`form-control ${
                      touched.phone && errors.phone ? "is-invalid" : ""
                    }`}
                    placeholder="Phone"
                  />
                  <ErrorMessage
                    name="phone"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>
                <br />

                {/* Shipping Method */}
                <div className="form-section">
                  <label>Shipping method</label>
                  <small style={{ display: "block", marginBottom: 8 }}>
                    Choose a shipping option — costs may vary by payment type.
                  </small>

                  <div style={{ display: "flex", gap: 12, flexDirection: "column" }}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setFieldValue("shipping_method", "bank_transfer")}
                      onKeyDown={(e) => e.key === "Enter" && setFieldValue("shipping_method", "bank_transfer")}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        borderRadius: 8,
                        cursor: "pointer",
                        border: values.shipping_method === "bank_transfer" ? "2px solid #7CCF5C" : "1px solid #e6e6e6",
                        background: "#fff",
                        position: "relative",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <input
                          type="radio"
                          name="shipping_method"
                          value="bank_transfer"
                          checked={values.shipping_method === "bank_transfer"}
                          onChange={() => setFieldValue("shipping_method", "bank_transfer")}
                        />
                        <div>
                          <div style={{ fontWeight: 600 }}>Bank Transfer | Save Rs. 200!</div>
                          <div style={{ fontSize: 13, color: "#666" }}>Pay by bank and save on shipping</div>
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: values.shipping_method === "bank_transfer" ? "#2b7a2b" : "#333", display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>FREE</span>
                        {values.shipping_method === "bank_transfer" && (
                          <i
                            className="bi bi-check-circle-fill"
                            style={{ color: "#2b7a2b", fontSize: 18 }}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>

                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setFieldValue("shipping_method", "cod")}
                      onKeyDown={(e) => e.key === "Enter" && setFieldValue("shipping_method", "cod")}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        borderRadius: 8,
                        cursor: "pointer",
                        border: values.shipping_method === "cod" ? "2px solid #7CCF5C" : "1px solid #e6e6e6",
                        background: "#fff",
                        position: "relative",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <input
                          type="radio"
                          name="shipping_method"
                          value="cod"
                          checked={values.shipping_method === "cod"}
                          onChange={() => setFieldValue("shipping_method", "cod")}
                        />
                        <div>
                          <div style={{ fontWeight: 600 }}>Cash on Delivery | Shipping Fee</div>
                          <div style={{ fontSize: 13, color: "#666" }}>Pay on delivery (extra shipping charge)</div>
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: values.shipping_method === "cod" ? "#2b7a2b" : "#333", display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{formatPKR(200)}</span>
                        {values.shipping_method === "cod" && (
                          <i
                            className="bi bi-check-circle-fill"
                            style={{ color: "#2b7a2b", fontSize: 18 }}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <br />

                {/* Payment */}
                <div className="form-section">
                  <label>Payment</label>
                  <small style={{ fontSize: "14px" }}>
                    All transactions are secure and encrypted.
                  </small>

                  <Accordion alwaysOpen>
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <span className="me-2">
                          <i className="bi bi-bank2 custom-accordion-icon"></i>
                        </span>
                        Bank Deposit
                      </Accordion.Header>
                      <Accordion.Body>
                        IBAN: PK95MEZN0002460107600100 <br />
                        Account Title:<br /> MOHAMMAD MOHSIN AMEER
                        <br />
                        Account Number: 02460107600100
                        <br />
                        Bank: Meezan Bank
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>
                <br />

                <button
                  type="submit"
                  className="btn-pay"
                  disabled={cloader || isSubmitting}
                >
                  Complete Order
                </button>

                <p className="privacy">All Rights Reserved Aura Skin Premium</p>
              </div>

              {/* RIGHT PANEL */}
              <div className="checkout-right">
                {cartItems.map((item, i) => (
                  <div className="cart-item" key={i}>
                    <div className="cart-badge-wrapper">
                      <img
                        src={item.product.imageUrl}
                        alt="Cart"
                        className="img-thumbnail"
                      />
                      {cartItemsCounter > 0 && (
                        <span className="cart-badge">{cartItemsCounter}</span>
                      )}
                    </div>
                    <div className="cart-details">
                      <div className="cart-title">{item.product.title}</div>
                    </div>
                    <div className="cart-price">
                      {formatPKR(item.product.price * item.quantity)}
                    </div>
                  </div>
                ))}

                {/* ✅ referral_code_used inside summary but Formik-controlled */}
                <div className="form-section">
                  <Field
                    type="text"
                    name="referral_code_used"
                    className={`form-control ${
                      touched.referral_code_used && errors.referral_code_used
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder="Referral Code"
                  />
                  <ErrorMessage
                    name="referral_code_used"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>

                <div className="cart-total">
                  <div className="line">
                    <span>Subtotal</span>
                    <span>{formatPKR(subtotal)}</span>
                  </div>
                  <div className="line">
                    <span>Shipping</span>
                    {/* shipping fee depends on selected shipping_method */}
                    {(() => {
                      const shippingFee = values && values.shipping_method === "cod" ? 200 : 0;
                      return <span>{shippingFee === 0 ? "Free" : formatPKR(shippingFee)}</span>;
                    })()}
                  </div>
                  <div className="line total">
                    <span>Total</span>
                    {(() => {
                      const shippingFee = values && values.shipping_method === "cod" ? 200 : 0;
                      const computed = subtotal + subtotal * taxRate + shippingFee;
                      return <span>{formatPKR(computed)}</span>;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Container>
  );
}

// import React, { useEffect, useState } from "react";
// import "./Checkout.css";
// // import { getCart } from "../api/cartApi"; // Replace with your actual API method
// import { getAnonUserId, getHostName } from "../helpers/utils";
// import axios from "axios";
// import { Container, Form as BSForm } from "react-bootstrap";
// import { toast } from "react-toastify";
// import { Link, replace, useNavigate } from "react-router-dom";
// import { Formik, Form, Field, ErrorMessage } from "formik";
// import * as Yup from "yup";
// import Accordion from "react-bootstrap/Accordion";

// export default function CheckoutPage() {
//   let hostname = getHostName();
//   const navigate = useNavigate();

//   const [cartItems, setCartItems] = useState([]);
//   const [cartItemsCounter, setCartItemsCounter] = React.useState(0);

//   const [checkoutLoader, setCheckoutLoader] = React.useState(false);

//   const [subtotal, setSubtotal] = useState(0);
//   const taxRate = 0.0;

//   useEffect(() => {
//     const getAllCartListData = () => {
//       let anonUserId = getAnonUserId();
//       axios
//         .post(`${hostname}/api/cart/get`, {
//           annoUserDeviceId: anonUserId,
//         })
//         .then((res) => {
//           console.log(res.data);
//           let arr = [];
//           arr = res.data.filter((v) => v.status);
//           console.log(arr);

//           setCartItems(arr);
//         })
//         .catch((err) => {
//           console.log(err);
//         });
//     };

//     const getCartCounterData = () => {
//       let anonUserId = getAnonUserId();
//       axios
//         .get(`${hostname}/api/cart/count?annoUserDeviceId=${anonUserId}`)
//         .then((res) => {
//           console.log(res.data);
//           setCartItemsCounter(res.data.count);
//         })
//         .catch((err) => {
//           console.log(err);

//           if ("response" in err)
//             toast.error(err.response.data.error, { theme: "colored" });
//           else toast.error("something went wrong!", { theme: "colored" });
//         });
//     };
//     getCartCounterData();
//     getAllCartListData();
//   }, []);

//   useEffect(() => {
//     const subtotalCalc = cartItems.reduce(
//       (acc, item) => acc + item.product.price * item.quantity,
//       0
//     );
//     setSubtotal(subtotalCalc);
//   }, [cartItems]);

//   const total = (subtotal + subtotal * taxRate).toFixed(2);

//   const handleCheckoutSubmit = async (values, { setSubmitting, resetForm }) => {
//     try {
//       let anonUserId = getAnonUserId();

//       console.log("Submitting checkout form...", values);

//       const res = await axios.post(`${hostname}/api/checkout/submit`, {
//         ...values,
//         annoUserDeviceId: anonUserId, // For guest binding
//       });

//       toast.success("Order placed successfully!");
//       toast.success("A confirmation email will find ou shortly.");
//       // Optionally clear cart in localStorage / context / server
//       resetForm();
//       setSubmitting(false);
//       setTimeout(() => {
//         navigate("/", { replace: true });
//       }, 500);
//     } catch (err) {
//       console.error("Order submit error:", err);
//       alert("⚠️ Something went wrong.");
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <Container>
//       <div className="shopify-checkout">
//         <div className="checkout-left">
//           <div className="brand">
//             <span onClick={() => navigate("/", { replace: true })}>
//               <i
//                 class="bi bi-arrow-left-circle"
//                 style={{ cursor: "pointer" }}
//               ></i>
//             </span>
//             <h5 className="fw-bold ms-3">AuraSkinPremium</h5>
//           </div>

//           <Formik
//             initialValues={{
//             //  fill here all state values

//             }}
//             validationSchema={Yup.object().shape({
//               email: Yup.string()
//                 .email("Enter valid email")
//                 .required("Email is required"),
//               card: Yup.string()
//                 .min(16, "Card number is short")
//                 .required("Card number required"),
//               expiry: Yup.string().required("Expiry required"),
//               cvc: Yup.string().min(3, "CVC is short").required("CVC required"),
//               name: Yup.string().required("Cardholder name required"),
//               country: Yup.string().required("Country required"),
//               address: Yup.string().required("Address required"),
//               apartment: Yup.string(), // Optional
//               city: Yup.string().required("City required"),
//               postal: Yup.string().required("Postal code required"),
//             })}
//             onSubmit={handleCheckoutSubmit}
//           >
//             {({ handleChange, handleBlur, values }) => (
//               <Form className="checkout-form">
//                 {/* Contact */}
//                 <div className="form-section">
//                   <label className="d-flex justify-content-between align-items-center">
//                     <span>Contact</span>
//                     <Link to={"/login"} className="login-link">
//                       Log in
//                     </Link>
//                   </label>
//                   <Field
//                     type="email"
//                     name="email"
//                     className="form-control"
//                     placeholder="Email address"
//                   />
//                   <ErrorMessage
//                     name="email"
//                     component="div"
//                     className="text-danger fs--1"
//                     style={{ fontSize: "12px" }}
//                   />
//                 </div>

//                 {/* Billing */}
//                 <div className="form-section">
//                   <label>Delivery</label>
//                   <select
//                     value={values.country}
//                     onChange={handleChange}
//                     onBlur={handleBlur}
//                     className="form-select"
//                     name="country"
//                   >
//                     <option value="Pakistan">Pakistan</option>
//                   </select>
//                   <ErrorMessage
//                     name="country"
//                     component="div"
//                     className="text-danger fs--1"
//                     style={{ fontSize: "12px" }}
//                   />
//                   <div className="row-2">
//                     <Field
//                       type="text"
//                       name="first_name"
//                       className="form-control"
//                       placeholder="First Name"
//                     />
//                     <Field
//                       type="text"
//                       name="last_name"
//                       className="form-control"
//                       placeholder="last Name"
//                     />
//                   </div>
//                   <Field
//                     type="text"
//                     className="form-control"
//                     name="address"
//                     placeholder="Address"
//                   />
//                   <ErrorMessage
//                     name="address"
//                     component="div"
//                     className="text-danger fs--1"
//                     style={{ fontSize: "12px" }}
//                   />

//                   <div className="row-2">
//                     <Field
//                       type="text"
//                       name="city"
//                       className="form-control"
//                       placeholder="City"
//                     />
//                     <Field
//                       type="text"
//                       className="form-control"
//                       name="postal"
//                       placeholder="Postal code"
//                     />
//                   </div>
//                   <ErrorMessage
//                     name="city"
//                     component="div"
//                     className="text-danger fs--1"
//                     style={{ fontSize: "12px" }}
//                   />
//                   <ErrorMessage
//                     name="postal"
//                     component="div"
//                     className="text-danger fs--1"
//                     style={{ fontSize: "12px" }}
//                   />
//                   <Field
//                     type="text"
//                     name="phone"
//                     className="form-control"
//                     placeholder="Phone"
//                   />
//                 </div>
//                 <div className="form-section">
//                   <label>Payment</label>
//                   <small style={{ fontSize: "14px" }}>
//                     All transactions are secure and encrypted.
//                   </small>

//                   <Accordion>
//                     <Accordion.Item eventKey="0">
//                       <Accordion.Header>Bank Deposit</Accordion.Header>
//                       <Accordion.Body>
//                         IBAN: 11111111111111111111
//                         <br />
//                         Account Title: Moeein Khan
//                         <br />
//                         Account Number: 2323224234233
//                         <br />
//                         Bank: Bank Name
//                       </Accordion.Body>
//                     </Accordion.Item>
//                   </Accordion>

//                   <ErrorMessage
//                     name="address"
//                     component="div"
//                     className="text-danger fs--1"
//                     style={{ fontSize: "12px" }}
//                   />
//                 </div>

//                 <button type="submit" className="btn-pay">
//                   Complete Order
//                 </button>
//               </Form>
//             )}
//           </Formik>

//           <p className="privacy">All Rights Reserved Aura Skin Premium</p>
//         </div>

//         <div className="checkout-right">
//           {cartItems.map((item, i) => (
//             <div className="cart-item" key={i}>
//               <div className="cart-badge-wrapper">
//                 <img
//                   //   src="/img/icons/cart.svg"
//                   src={item.product.imageUrl}
//                   alt="Cart"
//                   className="img-thumbnail"
//                   //   style={{ width: "32px", height: "32px" }}
//                 />
//                 {cartItemsCounter > 0 && (
//                   <span className="cart-badge">{cartItemsCounter}</span>
//                 )}
//               </div>
//               {/* <img src={item.product.imageUrl} alt="" /> */}
//               <div className="cart-details">
//                 <div className="cart-title">{item.product.title}</div>
//                 {/* <div className="cart-meta">
//                   Qty: {item.quantity}{" "}
//                   &nbsp; | &nbsp; {item.product.packSize}
//                 </div> */}
//               </div>
//               <div className="cart-price">
//                 Rs {item.product.price * item.quantity}
//               </div>
//             </div>
//           ))}
//           <div className="form-section">
//             <BSForm.Control placeholder="referral_code_used Code" />
//           </div>
//           <div className="cart-total">
//             <div className="line">
//               <span>Subtotal</span>
//               <span>Rs {subtotal.toFixed(2)}</span>
//             </div>

//             <div className="line">
//               <span>Shipping</span>
//               <span>Free</span>
//             </div>
//             <div className="line total">
//               <span>Total</span>
//               <span>PKR Rs {total}</span>
//             </div>
//           </div>
//         </div>
//       </div>
//     </Container>
//   );
// }
